(() => {
  if (customElements.get('homepage-hero-banner-slideshow')) {
    return;
  }

  class HomepageHeroBannerSlideshow extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.viewport = null;
      this.paginationEl = null;
      this.prevZone = null;
      this.nextZone = null;
      this.videos = [];
      this.mediaQuery = window.matchMedia('(min-width: 768px)');
      this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.handleHoverPrev = this.handleHoverPrev.bind(this);
      this.handleHoverNext = this.handleHoverNext.bind(this);
      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.handleViewportMouseMove = this.handleViewportMouseMove.bind(this);
      this.handleViewportMouseLeave = this.handleViewportMouseLeave.bind(this);
      this.handleViewportClick = this.handleViewportClick.bind(this);
    }

    connectedCallback() {
      this.viewport = this.querySelector('.homepage-hero-banner__viewport');
      this.paginationEl = this.querySelector('[data-pagination]');
      this.prevZone = this.querySelector('[data-hover-prev]');
      this.nextZone = this.querySelector('[data-hover-next]');
      this.videos = Array.from(this.querySelectorAll('[data-hero-video]'));

      this.setupVideos();

      if (this.viewport && this.viewport.classList.contains('swiper') && typeof Swiper !== 'undefined') {
        this.initSwiper();
      } else {
        this.updateActiveVideo(null);
      }

      this.prevZone?.addEventListener('click', this.handleHoverPrev);
      this.nextZone?.addEventListener('click', this.handleHoverNext);
      this.mediaQuery.addEventListener('change', this.handleMediaChange);
      this.setupHoverNavigation();
    }

    disconnectedCallback() {
      this.prevZone?.removeEventListener('click', this.handleHoverPrev);
      this.nextZone?.removeEventListener('click', this.handleHoverNext);
      this.mediaQuery.removeEventListener('change', this.handleMediaChange);

      if (this.viewport) {
        this.viewport.removeEventListener('mousemove', this.handleViewportMouseMove);
        this.viewport.removeEventListener('mouseleave', this.handleViewportMouseLeave);
        this.viewport.removeEventListener('click', this.handleViewportClick);
      }

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      this.videos.forEach((video) => {
        video.pause();
      });
    }

    setupHoverNavigation() {
      const enableHoverArrows = Boolean(this.prevZone || this.nextZone);
      if (!this.viewport || !enableHoverArrows || !this.hoverQuery.matches) {
        return;
      }

      this.viewport.addEventListener('mousemove', this.handleViewportMouseMove);
      this.viewport.addEventListener('mouseleave', this.handleViewportMouseLeave);
      this.viewport.addEventListener('click', this.handleViewportClick);
    }

    getHoveredHalf(event) {
      const rect = this.viewport.getBoundingClientRect();
      return event.clientX - rect.left < rect.width / 2 ? 'prev' : 'next';
    }

    handleViewportMouseMove(event) {
      const half = this.getHoveredHalf(event);
      this.viewport.classList.toggle('is-cursor-prev', half === 'prev');
      this.viewport.classList.toggle('is-cursor-next', half === 'next');
    }

    handleViewportMouseLeave() {
      this.viewport.classList.remove('is-cursor-prev', 'is-cursor-next');
    }

    handleViewportClick(event) {
      if (event.target.closest('.homepage-hero-banner__content')) {
        return;
      }

      if (this.getHoveredHalf(event) === 'prev') {
        this.swiper?.slidePrev();
      } else {
        this.swiper?.slideNext();
      }
    }

    setupVideos() {
      this.videos.forEach((video) => {
        this.applyVideoSource(video);
        video.addEventListener('loadedmetadata', () => {
          if (!video.duration || !isFinite(video.duration)) {
            return;
          }

          const durationMs = Math.round(video.duration * 1000);
          const slide = video.closest('[data-slide]');
          if (!slide) {
            return;
          }

          slide.dataset.swiperAutoplay = String(durationMs);

          if (this.swiper && slide.classList.contains('swiper-slide-active')) {
            this.swiper.params.autoplay.delay = durationMs;
          }
        });
      });
    }

    applyVideoSource(video) {
      const desktopSrc = video.dataset.srcDesktop || '';
      const mobileSrc = video.dataset.srcMobile || '';
      const nextSrc = this.mediaQuery.matches ? desktopSrc || mobileSrc : mobileSrc || desktopSrc;

      if (!nextSrc || video.dataset.currentSrc === nextSrc) {
        return;
      }

      video.dataset.currentSrc = nextSrc;
      video.setAttribute('preload', 'metadata');
      video.src = nextSrc;
      video.load();

      const slide = video.closest('[data-slide]');
      if (slide && (!this.swiper || slide.classList.contains('swiper-slide-active'))) {
        video.play().catch(() => {});
      }
    }

    handleMediaChange() {
      this.videos.forEach((video) => this.applyVideoSource(video));
    }

    initSwiper() {
      const paginationOptions = this.paginationEl ? { el: this.paginationEl, clickable: true } : false;

      this.swiper = new Swiper(this.viewport, {
        loop: true,
        speed: 700,
        effect: 'fade',
        fadeEffect: { crossFade: true },
        autoplay: window.homepageHeroBannerAutoplay
          ? { delay: 6000, disableOnInteraction: false, pauseOnMouseEnter: false }
          : false,
        touchRatio: 1,
        touchAngle: 45,
        grabCursor: false,
        pagination: paginationOptions,
        on: {
          autoplayStart: (swiper) => {
            swiper.el.classList.add('slideshow-started');
          },
          autoplayTimeLeft: (swiper, timeLeft, percentage) => {
            const activeBullet = swiper.pagination && swiper.pagination.bullets
              ? swiper.pagination.bullets[swiper.realIndex]
              : null;
            if (activeBullet) {
              activeBullet.style.setProperty('--progress', String(1 - percentage));
            }
          },
          slideChange: (swiper) => {
            this.resetPaginationProgress(swiper);
          },
          slideChangeTransitionStart: (swiper) => {
            this.updateActiveVideo(swiper);
          },
        },
      });

      this.updateActiveVideo(this.swiper);
    }

    resetPaginationProgress(swiper) {
      if (!swiper.pagination || !swiper.pagination.bullets) {
        return;
      }

      swiper.pagination.bullets.forEach((bullet) => {
        bullet.style.setProperty('--progress', '0');
      });
    }

    updateActiveVideo(swiper) {
      const slides = Array.from(this.querySelectorAll('[data-slide]'));

      slides.forEach((slide) => {
        const video = slide.querySelector('[data-hero-video]');
        if (!video) {
          return;
        }

        const isActive = !swiper || slide.classList.contains('swiper-slide-active');

        if (isActive) {
          video.currentTime = 0;
          video.play().catch(() => {});
        } else {
          video.pause();
          video.currentTime = 0;
        }
      });
    }

    handleHoverPrev() {
      this.swiper?.slidePrev();
    }

    handleHoverNext() {
      this.swiper?.slideNext();
    }
  }

  customElements.define('homepage-hero-banner-slideshow', HomepageHeroBannerSlideshow);
})();
